import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  DollarSign, Plus, Trash2, Edit2, Receipt, Car, Download, 
  Calendar, X, Camera, Filter, TrendingUp, AlertCircle, Lock
} from 'lucide-react';
import { format, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Expenses() {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [mileageRate, setMileageRate] = useState(0.67);
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [filter, setFilter] = useState('month'); // 'month', 'year', 'all'
  const [categoryFilter, setCategoryFilter] = useState('');
  const fileInputRef = useRef(null);
  
  const [form, setForm] = useState({
    amount: '',
    description: '',
    category: 'materials',
    receipt_photo: '',
    mileage: '',
    is_billable: false,
    expense_date: format(new Date(), 'yyyy-MM-dd')
  });

  useEffect(() => {
    fetchCategories();
    fetchLimits();
  }, []);

  useEffect(() => {
    fetchExpenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, categoryFilter]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/expense-categories`);
      setCategories(response.data.categories || []);
      setMileageRate(response.data.mileage_rate || 0.67);
    } catch (error) {
      console.error('Failed to fetch categories');
    }
  };

  const fetchLimits = async () => {
    try {
      const response = await axios.get(`${API}/expenses/limits`);
      setLimits(response.data);
    } catch (error) {
      console.error('Failed to fetch limits');
    }
  };

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ general_only: 'true' });
      
      // Date filters
      const now = new Date();
      if (filter === 'month') {
        params.append('start_date', format(startOfMonth(now), 'yyyy-MM-dd'));
        params.append('end_date', format(endOfMonth(now), 'yyyy-MM-dd'));
      } else if (filter === 'year') {
        params.append('start_date', format(startOfYear(now), 'yyyy-MM-dd'));
        params.append('end_date', format(endOfYear(now), 'yyyy-MM-dd'));
      }
      
      if (categoryFilter) {
        params.append('category', categoryFilter);
      }
      
      const response = await axios.get(`${API}/expenses?${params}`);
      setExpenses(response.data.expenses || []);
      setSummary(response.data.summary || null);
    } catch (error) {
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.amount || !form.description || !form.category) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount),
        mileage: form.mileage ? parseFloat(form.mileage) : null,
        job_id: null // General expense
      };

      if (editingExpense) {
        await axios.put(`${API}/expenses/${editingExpense.id}`, payload);
        toast.success('Expense updated');
      } else {
        await axios.post(`${API}/expenses`, payload);
        toast.success('Expense added');
      }

      setShowModal(false);
      resetForm();
      fetchExpenses();
      fetchLimits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save expense');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await axios.delete(`${API}/expenses/${id}`);
      toast.success('Expense deleted');
      fetchExpenses();
      fetchLimits();
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setForm({
      amount: expense.amount.toString(),
      description: expense.description,
      category: expense.category,
      receipt_photo: expense.receipt_photo || '',
      mileage: expense.mileage?.toString() || '',
      is_billable: expense.is_billable,
      expense_date: expense.expense_date
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingExpense(null);
    setForm({
      amount: '',
      description: '',
      category: 'materials',
      receipt_photo: '',
      mileage: '',
      is_billable: false,
      expense_date: format(new Date(), 'yyyy-MM-dd')
    });
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!limits?.has_receipt_photos) {
      toast.error('Receipt photos require Solo Pro plan');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setForm(prev => ({ ...prev, receipt_photo: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleExportCSV = async () => {
    try {
      const now = new Date();
      let params = '';
      if (filter === 'month') {
        params = `?start_date=${format(startOfMonth(now), 'yyyy-MM-dd')}&end_date=${format(endOfMonth(now), 'yyyy-MM-dd')}`;
      } else if (filter === 'year') {
        params = `?start_date=${format(startOfYear(now), 'yyyy-MM-dd')}&end_date=${format(endOfYear(now), 'yyyy-MM-dd')}`;
      }
      
      const response = await axios.get(`${API}/expenses/export/csv${params}`);
      
      // Download CSV
      const blob = new Blob([response.data.csv_content], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Expenses exported');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const isPaidPlan = user?.subscription_tier !== 'free';

  return (
    <div className="p-4 pb-24 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-dark">Expenses</h1>
          <p className="text-slate-500 text-sm">Track business expenses for tax time</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setShowModal(true); }}
          className="bg-[#FF5F00]"
          data-testid="add-expense-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add
        </Button>
      </div>

      {/* Limits Warning */}
      {limits && !limits.can_add && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 mb-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-amber-800">Monthly Limit Reached</p>
            <p className="text-sm text-amber-700">
              You've used {limits.current_count}/{limits.max_allowed} expenses this month.
              <button className="text-[#FF5F00] font-bold ml-1 underline">Upgrade to Solo Pro</button> for unlimited.
            </p>
          </div>
        </div>
      )}

      {/* Summary Card */}
      {summary && (
        <div className="bg-white rounded-xl border-2 border-slate-200 shadow-hard p-4 mb-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-red-50 rounded-lg p-3">
              <p className="text-xs text-red-600 font-bold uppercase">Total Expenses</p>
              <p className="font-heading text-2xl font-bold text-red-700">${summary.total_amount.toFixed(2)}</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-3">
              <p className="text-xs text-blue-600 font-bold uppercase">Mileage Value</p>
              <p className="font-heading text-2xl font-bold text-blue-700">${summary.total_mileage_value.toFixed(2)}</p>
              <p className="text-xs text-blue-500">{summary.total_mileage.toFixed(1)} miles</p>
            </div>
          </div>
          
          {/* Category Breakdown */}
          {summary.category_breakdown?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-200">
              <p className="text-xs font-bold text-slate-500 uppercase mb-2">By Category</p>
              <div className="space-y-2">
                {summary.category_breakdown.map((cat, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-slate-600">{cat.name}</span>
                    <span className="font-bold text-slate-900">${cat.amount.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
        <Button
          variant={filter === 'month' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('month')}
          className={filter === 'month' ? 'bg-[#FF5F00]' : ''}
        >
          This Month
        </Button>
        <Button
          variant={filter === 'year' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('year')}
          className={filter === 'year' ? 'bg-[#FF5F00]' : ''}
        >
          This Year
        </Button>
        <Button
          variant={filter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('all')}
          className={filter === 'all' ? 'bg-[#FF5F00]' : ''}
        >
          All Time
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExportCSV}
          className="ml-auto"
        >
          <Download className="w-4 h-4 mr-1" /> CSV
        </Button>
      </div>

      {/* Category Filter */}
      <div className="mb-4">
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="w-full h-10 px-3 border-2 border-slate-200 rounded-lg bg-white text-sm"
        >
          <option value="">All Categories</option>
          {categories.map(cat => (
            <option key={cat.id} value={cat.id}>{cat.name}</option>
          ))}
        </select>
      </div>

      {/* Expense List */}
      {loading ? (
        <div className="text-center py-8 text-slate-400">Loading...</div>
      ) : expenses.length === 0 ? (
        <div className="text-center py-12">
          <Receipt className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p className="text-slate-500">No expenses yet</p>
          <p className="text-sm text-slate-400">Add your first business expense</p>
        </div>
      ) : (
        <div className="space-y-3">
          {expenses.map(expense => (
            <div 
              key={expense.id}
              className="bg-white rounded-xl border-2 border-slate-200 shadow-hard p-4"
              data-testid={`expense-${expense.id}`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <p className="font-bold text-slate-900">{expense.description}</p>
                  <p className="text-sm text-slate-500">{expense.category_name}</p>
                </div>
                <div className="text-right">
                  <p className="font-heading text-xl font-bold text-red-600">
                    -${expense.amount.toFixed(2)}
                  </p>
                  {expense.mileage && (
                    <p className="text-xs text-blue-600">
                      +{expense.mileage} mi (${expense.mileage_value?.toFixed(2)})
                    </p>
                  )}
                </div>
              </div>
              
              <div className="flex items-center justify-between text-xs text-slate-400">
                <span>{format(new Date(expense.expense_date), 'MMM d, yyyy')}</span>
                <div className="flex items-center gap-2">
                  {expense.receipt_photo && (
                    <span className="text-emerald-600 flex items-center gap-1">
                      <Camera className="w-3 h-3" /> Receipt
                    </span>
                  )}
                  <button 
                    onClick={() => handleEdit(expense)}
                    className="p-1 hover:bg-slate-100 rounded"
                  >
                    <Edit2 className="w-4 h-4 text-slate-500" />
                  </button>
                  <button 
                    onClick={() => handleDelete(expense.id)}
                    className="p-1 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-bold">
                {editingExpense ? 'Edit Expense' : 'Add Expense'}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-2">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Amount */}
              <div>
                <Label className="text-xs font-bold uppercase text-slate-500">Amount *</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="number"
                    step="0.01"
                    value={form.amount}
                    onChange={(e) => setForm(prev => ({ ...prev, amount: e.target.value }))}
                    className="pl-9 h-12"
                    placeholder="0.00"
                    data-testid="expense-amount"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <Label className="text-xs font-bold uppercase text-slate-500">Description *</Label>
                <Input
                  value={form.description}
                  onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                  className="h-12"
                  placeholder="What was this expense for?"
                  data-testid="expense-description"
                />
              </div>

              {/* Category */}
              <div>
                <Label className="text-xs font-bold uppercase text-slate-500">Category (IRS Schedule C) *</Label>
                <select
                  value={form.category}
                  onChange={(e) => setForm(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full h-12 px-3 border-2 border-slate-200 rounded-lg bg-white"
                  data-testid="expense-category"
                >
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              {/* Date */}
              <div>
                <Label className="text-xs font-bold uppercase text-slate-500">Date</Label>
                <Input
                  type="date"
                  value={form.expense_date}
                  onChange={(e) => setForm(prev => ({ ...prev, expense_date: e.target.value }))}
                  className="h-12"
                  data-testid="expense-date"
                />
              </div>

              {/* Mileage */}
              <div className={!limits?.has_mileage ? 'opacity-50' : ''}>
                <Label className="text-xs font-bold uppercase text-slate-500 flex items-center gap-2">
                  <Car className="w-4 h-4" /> Mileage
                  {!limits?.has_mileage && <Lock className="w-3 h-3" />}
                </Label>
                <div className="flex gap-2 items-center">
                  <Input
                    type="number"
                    step="0.1"
                    value={form.mileage}
                    onChange={(e) => setForm(prev => ({ ...prev, mileage: e.target.value }))}
                    className="h-12 flex-1"
                    placeholder="Miles driven"
                    disabled={!limits?.has_mileage}
                    data-testid="expense-mileage"
                  />
                  {form.mileage && (
                    <span className="text-sm text-blue-600 font-bold whitespace-nowrap">
                      = ${(parseFloat(form.mileage || 0) * mileageRate).toFixed(2)}
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-400 mt-1">IRS rate: ${mileageRate}/mile</p>
              </div>

              {/* Receipt Photo */}
              <div className={!limits?.has_receipt_photos ? 'opacity-50' : ''}>
                <Label className="text-xs font-bold uppercase text-slate-500 flex items-center gap-2">
                  <Camera className="w-4 h-4" /> Receipt Photo
                  {!limits?.has_receipt_photos && <Lock className="w-3 h-3" />}
                </Label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  ref={fileInputRef}
                  disabled={!limits?.has_receipt_photos}
                />
                {form.receipt_photo ? (
                  <div className="relative">
                    <img 
                      src={form.receipt_photo} 
                      alt="Receipt" 
                      className="w-full h-32 object-cover rounded-lg border-2 border-slate-200"
                    />
                    <button
                      onClick={() => setForm(prev => ({ ...prev, receipt_photo: '' }))}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full h-12"
                    disabled={!limits?.has_receipt_photos}
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    {limits?.has_receipt_photos ? 'Attach Receipt' : 'Upgrade for Receipt Photos'}
                  </Button>
                )}
              </div>

              {/* Upgrade prompt for free users */}
              {!isPaidPlan && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                  <p className="font-bold text-amber-800 mb-1">Unlock More Features</p>
                  <p className="text-amber-700">
                    Upgrade to Solo Pro for receipt photos, mileage tracking, and unlimited expenses.
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowModal(false)} className="flex-1 h-12">
                Cancel
              </Button>
              <Button onClick={handleSubmit} className="flex-1 h-12 bg-[#FF5F00]" data-testid="save-expense-btn">
                {editingExpense ? 'Update' : 'Add Expense'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
